import React from 'react';
import {
  Email,
  Lock,
  Visibility,
  Add,
  Search,
} from '@mui/icons-material';
import {
  TextField,
  InputAdornment,
  FormHelperText,
  Box,
  FormLabel,
} from '@mui/material';
import appColors from '../../app_colors';
import { PhoneSvg } from '../adornments/InputIcons';

const squaredInputStyle = {
  borderRadius: 2,
  backgroundColor: appColors.primaryAppBackground,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
      opacity: 0.5,
    },
    '&.Mui-disabled svg': {
      opacity: 0.5,
    },
  },
  '&:has(.Mui-disabled)': {
    opacity: 0.5,
  },
};

const sharedAutoFillProps = {
  WebkitBoxShadow: `0 0 0 1000px ${appColors.buttons.whiteBackground} inset`,
  WebkitTextFillColor: appColors.text.blackText,
  height: 8,
};

const roundedInputStyle = {
  borderRadius: 8,
  height: 40,
  justifyContent: 'center',
  backgroundColor: appColors.buttons.whiteBackground,
  alignItems: 'center',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
  },
  '& input': {
    '&:-webkit-autofill': sharedAutoFillProps,
    '&:-webkit-autofill:hover': sharedAutoFillProps,
    '&:-webkit-autofill:focus': sharedAutoFillProps,
    '&:-webkit-autofill:active': sharedAutoFillProps,
  },
};

/** Icon component map - if you want to use a different icon, please import and add to this list */
export const ICON_TYPES = {
  Email,
  Search,
  Lock,
  Visibility,
  Add,
  Phone: PhoneSvg,
};

const getIcon = (
  iconName,
  color,
  size,
  enableFill = false,
) => {
  const IconComponent = ICON_TYPES[iconName];

  return <IconComponent style={{ color, fontSize: size, ...(enableFill && { fill: color }) }} />;
};

export const BackrsTextField = ({
  value,
  variant = 'SQUARE',
  type,
  onChange,
  icon,
  placeholder,
  label,
  id,
  name,
  autoComplete,
  autoFocus,
  inputProps,
  sx,
  error,
  helperText,
  onEnterPress,
  disabled = false,
}) => {
  /*
   * ICONS
   *
   * We can take advantage of the icon prop's discriminated union type by saying that
   * as long as the icon exists and is not a RIGHT_ICON, we will render the left icon.
   *
   * This means that the left icon will show if we pass in a LEFT_ICON or BOTH_ICON type (and vice versa for the right icon).
   */
  const leftIcon = icon && icon?.type !== 'RIGHT_ICON' ? (
    <InputAdornment
      sx={{ cursor: icon.onLeftIconClick ? 'pointer' : 'default' }}
      position="start"
      onClick={icon.onLeftIconClick}
    >
      {getIcon(icon.leftIcon, icon.leftIconColor, icon.leftIconSize, icon?.enableFill)}
    </InputAdornment>
  ) : undefined;

  const rightIcon = icon && icon?.type !== 'LEFT_ICON' ? (
    <InputAdornment
      position="end"
      sx={{ cursor: icon.onRightIconClick ? 'pointer' : 'default' }}
      onClick={icon.onRightIconClick}
    >
      {getIcon(icon.rightIcon, icon.rightIconColor, icon.rightIconSize)}
    </InputAdornment>
  ) : undefined;

  const inputStyle = variant === 'SQUARE' ? squaredInputStyle : roundedInputStyle;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && onEnterPress) {
      onEnterPress();
    }
  };

  return (
    <Box>
      {label && <FormLabel>{label}</FormLabel>}
      <TextField
        margin="dense"
        fullWidth
        id={id}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: false }}
        error={!!error}
        InputProps={{
          startAdornment: leftIcon,
          endAdornment: rightIcon,
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={inputProps}
        sx={{ ...inputStyle, ...sx }}
        onKeyUp={handleKeyDown}
        disabled={disabled}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};
